import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Tags, Plus, Pencil, Trash2, Check, X, Palette } from "lucide-react";
import { toast } from "sonner";
import DeleteConfirmDialog from "../DeleteConfirmDialog";
import { SottotipologieEditor } from "./SottotipologieEditor";
import {
  TIPOLOGIA_COLORS,
  TIPOLOGIA_ICON_OPTIONS,
  TipologiaIcon,
  tipologiaColor,
} from "../../lib/tipologiaVisual";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Selettore combinato icona + colore in un popover
const VisualPicker = ({ colore, icona, onChange, testIdPrefix }) => {
  const [open, setOpen] = useState(false);
  const activeColor = colore || TIPOLOGIA_COLORS[0];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center border border-border transition-transform active:scale-95"
          style={{ backgroundColor: activeColor }}
          data-testid={`${testIdPrefix}-picker-btn`}
        >
          <TipologiaIcon name={icona} className="h-4 w-4 text-white" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 bg-card border-border" align="start">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-foreground/70">
          <Palette className="h-3.5 w-3.5" /> Colore
        </div>
        <div className="grid grid-cols-8 gap-1.5 mb-3">
          {TIPOLOGIA_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange({ colore: c, icona })}
              className={`h-6 w-6 rounded-full transition-transform hover:scale-110 ${activeColor === c ? "ring-2 ring-offset-2 ring-offset-card ring-foreground" : ""}`}
              style={{ backgroundColor: c }}
              data-testid={`${testIdPrefix}-color-${c}`}
            />
          ))}
        </div>
        <div className="mb-2 text-xs font-medium text-foreground/70">Icona</div>
        <div className="grid grid-cols-8 gap-1.5 max-h-32 overflow-y-auto">
          {TIPOLOGIA_ICON_OPTIONS.map((ic) => (
            <button
              key={ic}
              type="button"
              onClick={() => onChange({ colore: activeColor, icona: ic })}
              className={`h-7 w-7 rounded-md flex items-center justify-center border transition-colors ${icona === ic ? "border-foreground bg-accent" : "border-border hover:bg-accent"}`}
              data-testid={`${testIdPrefix}-icon-${ic}`}
            >
              <TipologiaIcon name={ic} className="h-4 w-4 text-foreground" />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const TipologieUscitaDialog = ({ open, onOpenChange, tipologie, sottotipologie = [], getAuthHeaders, onChanged }) => {
  const [nuovoNome, setNuovoNome] = useState("");
  const [nuovoColore, setNuovoColore] = useState(TIPOLOGIA_COLORS[0]);
  const [nuovaIcona, setNuovaIcona] = useState("tag");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editNome, setEditNome] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const handleCreate = async () => {
    const nome = nuovoNome.trim();
    if (!nome) { toast.error("Inserisci un nome"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/tipologie-uscita`, { nome, colore: nuovoColore, icona: nuovaIcona }, { headers: getAuthHeaders() });
      toast.success("Tipologia creata");
      setNuovoNome("");
      setNuovoColore(TIPOLOGIA_COLORS[0]);
      setNuovaIcona("tag");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella creazione");
    } finally { setSaving(false); }
  };

  const startEdit = (t) => { setEditId(t.id); setEditNome(t.nome); };
  const cancelEdit = () => { setEditId(null); setEditNome(""); };

  const handleUpdate = async (id, patch) => {
    setSaving(true);
    try {
      await axios.put(`${API}/tipologie-uscita/${id}`, patch, { headers: getAuthHeaders() });
      if (patch.nome !== undefined) { toast.success("Tipologia aggiornata"); cancelEdit(); }
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'aggiornamento");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/tipologie-uscita/${deleteTarget.id}`, { headers: getAuthHeaders() });
      toast.success("Tipologia eliminata");
      setDeleteTarget(null);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'eliminazione");
      setDeleteTarget(null);
    }
  };

  const sorted = [...(tipologie || [])].sort((a, b) => (a.ordine || 0) - (b.ordine || 0));

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-card border-border" data-testid="tipologie-uscita-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Tags className="h-5 w-5 text-rose-500" />
              Tipologie di uscita
            </DialogTitle>
          </DialogHeader>

          <div className="flex gap-2">
            <VisualPicker
              colore={nuovoColore}
              icona={nuovaIcona}
              onChange={({ colore, icona }) => { setNuovoColore(colore); setNuovaIcona(icona); }}
              testIdPrefix="tipologia-nuovo"
            />
            <Input
              value={nuovoNome}
              onChange={(e) => setNuovoNome(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
              placeholder="Nuova tipologia (es. Marketing)"
              className="bg-card border-border text-foreground"
              data-testid="tipologia-nuovo-input"
            />
            <Button onClick={handleCreate} disabled={saving} className="gap-1 bg-rose-500 hover:bg-rose-400 text-white shrink-0" data-testid="tipologia-crea-btn">
              <Plus className="h-4 w-4" /> Aggiungi
            </Button>
          </div>

          <div className="mt-2 max-h-[45vh] overflow-y-auto divide-y divide-border rounded-xl border border-border">
            {sorted.length === 0 && (
              <div className="p-4 text-sm text-foreground/60 text-center">Nessuna tipologia.</div>
            )}
            {sorted.map((t, idx) => (
              <div key={t.id} data-testid={`tipologia-row-${t.id}`}>
              <div className="flex items-center gap-2 p-2.5 min-w-0">
                <VisualPicker
                  colore={tipologiaColor(t, idx)}
                  icona={t.icona}
                  onChange={(patch) => handleUpdate(t.id, patch)}
                  testIdPrefix={`tipologia-visual-${t.id}`}
                />
                {editId === t.id ? (
                  <>
                    <Input
                      value={editNome}
                      onChange={(e) => setEditNome(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && editNome.trim()) handleUpdate(t.id, { nome: editNome.trim() }); if (e.key === "Escape") cancelEdit(); }}
                      autoFocus
                      className="bg-card border-border text-foreground h-9"
                      data-testid={`tipologia-edit-input-${t.id}`}
                    />
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-lime-600" onClick={() => editNome.trim() && handleUpdate(t.id, { nome: editNome.trim() })} disabled={saving} data-testid={`tipologia-salva-btn-${t.id}`}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-foreground/60" onClick={cancelEdit}>
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 min-w-0 break-words text-foreground text-sm">{t.nome}</span>
                    <Button size="sm" variant="outline" className="text-xs shrink-0 px-2" data-testid={`tipologia-subtypes-${t.id}`} onClick={() => setExpandedId(expandedId === t.id ? null : t.id)} aria-expanded={expandedId === t.id}>Sotto ({sottotipologie.filter(s => s.tipologia_id === t.id).length})</Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-foreground/70 hover:text-foreground" onClick={() => startEdit(t)} data-testid={`tipologia-modifica-btn-${t.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500 hover:text-rose-400" onClick={() => setDeleteTarget(t)} data-testid={`tipologia-elimina-btn-${t.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
              {expandedId === t.id && <SottotipologieEditor tipologia={t} items={sottotipologie.filter(s => s.tipologia_id === t.id)} getAuthHeaders={getAuthHeaders} onChanged={onChanged} />}
              </div>
            ))}
          </div>
          <p className="text-xs text-foreground/50 mt-1">Tocca il quadratino colorato per scegliere colore e icona: renderanno più leggibili i grafici delle spese.</p>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Elimina tipologia"
        description={`Sei sicuro di voler eliminare "${deleteTarget?.nome}"? Se è usata da delle uscite, l'eliminazione verrà bloccata.`}
      />
    </>
  );
};
